<template>
  <div ref="chat_widget" class="chat-widget widget" style="position: relative">
    <!-- Accordion container used by b-collapse -->
    <div class="accordion" role="tablist"></div>

    <!-- HEADER of the chat widget -->
    <div class="widget-header" v-show="!isToolbarsCollapsed">
      <!-- Title and main icon -->
      <div
        class="icon-title"
        v-b-toggle="collapsible ? `main-accordion-${id}` : ''"
        v-if="!searchBarEnabled"
      >
        <img
          :src="require('@/assets/svg/chat.svg')"
          alt="Tenants"
          :style="{ width: '16px', marginInline: '16px' }"
        />
        <p class="text-center">{{ title }}</p>
      </div>

      <!-- Search bar toggle and container -->
      <div class="search-collapse" :class="[{ barEnable: searchBarEnabled }]">
        <img
          :src="require('@/assets/svg/search.svg')"
          alt="Search"
          :style="{ width: '18px', marginInline: '0.65rem' }"
          v-b-tooltip.hover.bottom.viewport="`${$t('Search')}`"
          @click="toggleSearchBar"
          v-if="!searchBarEnabled"
        />

        <div :class="['search-bar']" v-show="searchBarEnabled">
          <div
            ref="searchBarContainer"
            class="search-bar-container d-flex align-items-center"
            :style="`width: ${searchBarWidth}px;`"
          >
            <input
              type="text"
              v-model="searchQuery"
              ref="searchBarInput"
              class="form-control"
              :placeholder="$t('search')"
              @input="onTypeSearch"
            />
          </div>
          <feather-icon
            icon="XIcon"
            class="cursor-pointer"
            size="16"
            @click="toggleSearchBar"
          />
        </div>

        <!-- Optional text showing "results" or similar -->
        <span
          class="results-count"
          v-show="!sidebarMinWidthReached && searchBarEnabled"
        ></span>
      </div>

      <!-- If collapsible is allowed, show the chevron arrow -->
      <template v-if="collapsible">
        <feather-icon
          v-if="!isConfigModeOn && !searchBarEnabled"
          :icon="isCollapseOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'"
          v-b-toggle="collapsible ? `main-accordion-${id}` : ''"
          size="30"
        />
      </template>

      <!-- Draggable icon if in config mode -->
      <span class="handle cursor-move" v-if="isConfigModeOn">
        <feather-icon icon="MenuIcon" size="24" />
      </span>
    </div>
    <!-- /HEADER -->

    <!-- BODY -->
    <b-collapse
      :id="`main-accordion-${id}`"
      :accordion="`main-accordion-${id}`"
      role="tabpanel"
      :visible="false"
      v-model="isCollapseOpen"
      @input="onUpdateStatus"
      v-show="isCollapseOpen && !isToolbarsCollapsed"
    >
      <div
        :class="[
          'layouts-container d-flex flex-wrap justify-content-between chat-application',
          { 'flex-column': sidebarMinWidthReached },
        ]"
      >
        <div class="layout-container sidebar-content" style="width: 100%">
          <div
            class="chat-user-list-wrapper list-group scroll-area"
            style="width: 100%; height: auto"
          >
            <!-- APP/EMERGENCY GROUPS SECTION -->
            <div v-if="$can('read', 'EmergencyChat')">
              <h6 class="chat-list-title mb-1" style="margin-top: 1rem">
                <span>{{ $t("chat.emergency_groups") }}</span>
                <span>
                  <feather-icon
                    @click="getChatGroups()"
                    class="float-right cursor-pointer text-capitalize"
                    icon="RotateCcwIcon"
                    size="20"
                  />
                </span>
                <b-form-select
                  class="float-right mr-1"
                  size="sm"
                  style="width: 100px"
                  v-model="emergencyChatStatus"
                  :options="optionsEmergencyChatStatus"
                />
              </h6>
              <ul class="chat-users-list chat-list media-list">
                <!-- using 'appGroupsPaginated' for separate pagination -->
                <li
                  v-for="group in appGroupsPaginated"
                  :key="group.roomId"
                  @click="openChat(group)"
                >
                  <div
                    class="d-flex align-items-center"
                    style="width: 100vw"
                  >
                    <div>
                      <b-avatar
                        icon="people-fill"
                        size="35"
                        class="mr-1 cursor-pointer badge-minimal"
                        badge-variant="success"
                        variant="primary"
                      />
                    </div>
                    <div>
                      <h6 class="mb-0">
                        <span>{{ group.name }} : 
                          <small class="text-chat-status">
                            {{ group.chat_status_group == 'terminated' ? $t("chat.finalized") : $t("chat.assigned") }}
                          </small>
                        </span>
                      </h6>
                      <!-- Hide if user OR last_message is null -->
                      <p
                        v-if="group.user && group.last_message"
                        class="mb-0 truncate-text"
                      >
                        <span v-if="group.t">
                          {{
                            group.t == "au"
                              ? group.user +
                                `: ${$t("chat.added")} ` +
                                group.last_message
                              : group.user +
                                `: ${$t("chat.eliminated")} ` +
                                group.last_message
                          }}
                        </span>
                        <span v-else>
                          {{ group.user + ": " + group.last_message }}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div class="chat-meta text-nowrap">
                    <small class="float-right mb-25 chat-time">
                      {{
                        formatDateToMonthShort(group.updated_at, {
                          hour: "numeric",
                          minute: "numeric",
                        })
                      }}
                    </small>
                  </div>
                </li>
              </ul>

              <!-- PAGINATION for APP/EMERGENCY group list -->
              <pagination-widgets
                :sidebar-min-width-reached="sidebarMinWidthReached"
                :per-page.sync="perPageApp"
                :current-page.sync="currentPageApp"
                :total-records="appGroupsItems.length"
              />
            </div>
            <!-- REGULAR GROUPS TITLE -->
            <h6 class="chat-list-title" style="margin-top: 1rem">
              <span>{{ $t("chat.chats_group") }}</span>
              <span>
                <feather-icon
                  v-if="$can('create', 'ChatGroup')"
                  @click="showAddModal()"
                  :title="`${$t('actions.add')}`"
                  class="float-right cursor-pointer text-capitalize"
                  icon="PlusCircleIcon"
                  size="20"
                  v-b-tooltip.hover
                />
              </span>
            </h6>

            <!-- LIST OF REGULAR GROUPS -->
            <ul class="chat-users-list chat-list media-list">
              <!-- using 'regularGroupsPaginated' for separate pagination -->
              <li v-for="group in regularGroupsPaginated" :key="group.id">
                <div
                  class="d-flex align-items-center"
                  @click="openChat(group)"
                  style="width: 100vw"
                >
                  <div>
                    <b-avatar
                      icon="people-fill"
                      size="35"
                      class="mr-1 cursor-pointer badge-minimal"
                      badge-variant="success"
                      variant="primary"
                    />
                  </div>
                  <div>
                    <h6 class="mb-0">
                      <span>{{ group.name }}</span>
                    </h6>
                    <!-- Hide if user or last_message is null -->
                    <p
                      v-if="group.user && group.last_message"
                      class="mb-0 truncate-text"
                    >
                      <span v-if="group.t">
                        {{
                          group.t == "au"
                            ? group.user +
                              `: ${$t("chat.added")} ` +
                              group.last_message
                            : group.user +
                              `: ${$t("chat.eliminated")} ` +
                              group.last_message
                        }}
                      </span>
                      <span v-else>
                        {{ group.user + ": " + group.last_message }}
                      </span>
                    </p>
                  </div>
                </div>
                <div class="chat-meta text-nowrap">
                  <small class="float-right mb-25 chat-time">
                    {{
                      formatDateToMonthShort(group.updated_at, {
                        hour: "numeric",
                        minute: "numeric",
                      })
                    }}
                  </small>
                </div>
                <div class="ml-1" v-if="$can('update', 'ChatGroup')">
                  <b-dropdown
                    variant="link"
                    toggle-class="text-decoration-none p-0"
                    style="width: 100%"
                  >
                    <template #button-content>
                      <feather-icon
                        icon="MoreVerticalIcon"
                        size="15"
                        class="text-body float-right"
                      />
                    </template>

                    <!-- EDIT group -->
                    <b-dropdown-item
                      style="padding: 0px"
                      class="dropdown-item"
                      @click="showEditModal(group)"
                    >
                      <feather-icon icon="EditIcon" />
                      <span class="align-middle ml-50 text-capitalize">
                        {{ $t("actions.edit") }}
                      </span>
                    </b-dropdown-item>

                    <!-- DELETE group -->
                    <b-dropdown-item
                      v-if="$can('delete', 'ChatGroup')"
                      class="dropdown-item"
                      style="padding: 0px"
                      @click="confirmDelete(group)"
                    >
                      <feather-icon icon="TrashIcon" />
                      <span class="align-middle ml-50 text-capitalize">
                        {{ $t("actions.delete") }}
                      </span>
                    </b-dropdown-item>
                  </b-dropdown>
                </div>
              </li>
            </ul>

            <!-- PAGINATION for REGULAR group list  -->
            <pagination-widgets
              :sidebar-min-width-reached="sidebarMinWidthReached"
              :per-page.sync="perPageG"
              :current-page.sync="currentPageG"
              :total-records="regularGroupsItems.length"
            />

            <!-- CHATS TITLE -->
            <h6 class="chat-list-title" style="margin-top: 1rem">
              <span>{{ $t("chat.chats") }}</span>
            </h6>

            <!-- LIST OF CHATS (those with last_message) -->
            <ul class="chat-users-list chat-list media-list">
              <chat-contact
                v-for="chat in chatsItems"
                v-if="chat.last_message"
                :key="chat.id"
                :chat="chat"
                tag="li"
                is-chat-contact
                @click="openChat(chat)"
              />
            </ul>

            <!-- CONTACTS TITLE -->
            <h6 class="chat-list-title" style="margin-top: 1rem">
              <span>{{ $t("chat.contacts") }}</span>
            </h6>

            <!-- LIST OF CONTACTS (those without last_message) -->
            <ul class="chat-users-list contact-list media-list">
              <chat-contact
                v-for="chat in chatsItems"
                v-if="!chat.last_message"
                :key="chat.id"
                :chat="chat"
                tag="li"
                @click="openChat(chat)"
              />
            </ul>

            <!-- PAGINATION for chat list -->
            <pagination-widgets
              :sidebar-min-width-reached="sidebarMinWidthReached"
              :per-page.sync="perPage"
              :current-page.sync="currentPage"
              :total-records="chats.length"
            />
          </div>
        </div>
      </div>
    </b-collapse>

    <!-- MODAL FOR ADDING/EDITING A CHAT GROUP -->
    <div>
      <b-modal
        ref="previewModal"
        class="text-capitalize"
        hide-header
        hide-footer
        no-close-on-esc
        no-close-on-backdrop
        @show="getUsers()"
      >
        <h5 class="mb-0 text-capitalize">
          {{ isAdd ? $t("actions.add") : $t("actions.edit") }}
        </h5>
        <template>
          <validation-observer v-slot="{ handleSubmit }">
            <b-form
              class="auth-login-form mt-2"
              @submit.prevent="handleSubmit(onSubmit)"
              ref="createChatGroup"
            >
              <!-- GROUP NAME -->
              <validation-provider
                name="name"
                rules="required"
                v-slot="{ errors }"
              >
                <b-form-group
                  class="text-capitalize"
                  :label="$t('chat.group_name')"
                  label-for="name"
                >
                  <b-form-input id="name" v-model="name" trim />
                  <p v-if="errors" class="small text-danger">
                    {{ errors[0] }}
                  </p>
                </b-form-group>
              </validation-provider>

              <!-- GROUP USERS -->
              <validation-provider
                name="users"
                rules="required"
                v-slot="{ errors }"
              >
                <b-form-group
                  class="text-capitalize"
                  :label="$t('chat.users')"
                  label-for="users"
                >
                  <v-select
                    v-model="users"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="chatUsers"
                    :clearable="false"
                    :reduce="(val) => val.value"
                    input-id="tenant"
                    multiple
                  />
                  <p v-if="errors" class="small text-danger">
                    {{ errors[0] }}
                  </p>
                </b-form-group>
              </validation-provider>

              <b-button
                variant="primary"
                class="mr-1 text-capitalize"
                type="submit"
              >
                {{ isAdd ? `${$t("actions.add")}` : `${$t("actions.edit")}` }}
              </b-button>
              <b-button
                type="button"
                variant="outline-secondary"
                @click="onCancel"
                class="text-capitalize"
              >
                {{ $t("actions.cancel") }}
              </b-button>
            </b-form>
          </validation-observer>
        </template>
      </b-modal>
    </div>
  </div>
</template>

<script>
import { BAvatar } from "bootstrap-vue";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import store from "@/store";
import ChatContact from "./ChatContact.vue";
import LayoutApi from "@/libs/LayoutApi.js";
import { SLOT_TYPES } from "@/config/layoutConfig";
import axios from "@axios";
import { EventBus } from "@/libs/event-bus";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import { getUserData } from "@/auth/utils";
import { formatDateToMonthShort } from "@core/utils/filter";
import {
  ref,
  onMounted,
  onUnmounted,
  computed,
  watch,
} from "@vue/composition-api";
import Swal from "sweetalert2";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { debounce } from "lodash";
import PaginationWidgets from "@/views/components/GridView/widgets/components/PaginationWidgets.vue";
import { decryptData } from "@/utils/decryptUserDataUtils";

const layoutApi = new LayoutApi(store);

export default {
  components: {
    BAvatar,
    VuePerfectScrollbar,
    ChatContact,
    ValidationProvider,
    ValidationObserver,
    vSelect,
    PaginationWidgets,
  },
  props: {
    id: {
      type: [Number, String],
      default: 1,
    },
    location: {
      type: String,
      default: "sideBar",
    },
    title: {
      type: [String, Number],
    },
    isOpen: {
      type: Boolean,
      default: true,
    },
    parent: {
      type: [String, Number],
      default: "sidebar",
    },
  },
  setup(props, { emit }) {
    // ----------------------------------------
    // REFS & STATE
    // ----------------------------------------
    const collapsible = ref(true);
    const toast = useToast();
    const previewModal = ref(null);
    const userData = ref(getUserData());
    const isCollapseOpen = ref(true);
    const chats = ref([]);
    const socket = ref(null);
    const message_id = ref(1);
    const isLoading = ref(false);
    const error = ref(false);
    const chatGroup = ref([]);
    const chatUsers = ref([]);
    const users = ref([]);
    const name = ref(null);
    const isAdd = ref(true);
    const id_group = ref(null);
    const userChatId = ref(null);
    const chat_widget = ref(null);
    const searchBarEnabled = ref(false);
    const searchQuery = ref("");
    //pagination for regular groups:
    const perPageG = ref(4);
    const currentPageG = ref(1);
    //pagination for chats (direct contacts):
    const perPage = ref(4);
    const currentPage = ref(1);

    const perPageApp = ref(4); // how many app groups per page
    const currentPageApp = ref(1); // current page for app groups

    const searchBarWidth = ref(0);
    const chatAppGroup = ref([]);
    const tempChatAppGroup = ref([]);

    //Transalations
    const { t } = useI18nUtils();
    const trans = {
      "messages.delete.title": t("messages.delete.title"),
      "actions.delete": t("actions.delete"),
      "actions.cancel": t("actions.cancel"),
      "chat.error": t("chat.error"),
      "messages.loading.title": t("messages.loading.title"),
      "chat.finalized": t("chat.finalized"),
      "chat.assigned": t("chat.assigned"),
      "chat.all": t("chat.all"),
    };

    // ----------------------------------------
    // COMPUTED
    // ----------------------------------------

    //Config mode (when the layout changes)
    const isConfigModeOn = computed(
      () => store.state.psimConfig.configModeStatus
    );

    //to detect if it the sidebar is in the minimun
    const sidebarMinWidthReached = computed(
      () => store.getters["grid/sidebarMinWidthReached"]
    );

    //show hidde toolbars(global)
    const isToolbarsCollapsed = computed(
      () => store.state.psimConfig.isToolbarsCollapsed
    );

    //actual width of the side bar
    const sidebarWidth = computed(() => store.state.grid.sidebarWidth);

    //Observs the sidebar width to calculate the with of the search bar
    watch(sidebarWidth, () => {
      if (chat_widget.value) {
        searchBarWidth.value = chat_widget.value.offsetWidth - 20;
      }
    });

    //Loading or error message
    const message = computed(() => {
      if (error.value) return trans["chat.error"];
      return `${trans["messages.loading.title"]}...`;
    });

    // Sliced/paginated items for direct user "chats"
    const chatsItems = computed(() => {
      return chats.value.slice(
        (currentPage.value - 1) * perPage.value,
        currentPage.value * perPage.value
      );
    });

    // All group items. Next, we filter them by isFromApp or not.
    const chatGroupItems = computed(() => {
      return chatGroup.value; // We no longer slice *this* because we want separate pagination below.
    });

    // All elements of app chats.
    const chatAppGroupItems = computed(() => {
      return tempChatAppGroup.value;
    });

    // Distinguish app groups from regular groups:
    const regularGroupsItems = computed(() => {
      return chatGroupItems.value.filter((g) => !g.isFromApp);
    });
    const appGroupsItems = computed(() => {
      return chatGroupItems.value.filter((g) => g.isFromApp);
    });

    //Paginated array for app groups:
    const appGroupsPaginated = computed(() => {
      const start = (currentPageApp.value - 1) * perPageApp.value;
      return chatAppGroupItems.value.slice(start, start + perPageApp.value);
    });

    //Paginated array for regular groups:
    const regularGroupsPaginated = computed(() => {
      const start = (currentPageG.value - 1) * perPageG.value;
      return regularGroupsItems.value.slice(start, start + perPageG.value);
    });

    // ----------------------------------------
    // LIFECYCLE
    // ----------------------------------------
    onMounted(async () => {
      //adjust width of the search bar from the widget size
      if (chat_widget.value) {
        searchBarWidth.value = chat_widget.value.offsetWidth - 20;
      }
      isCollapseOpen.value = props.isOpen;

      try {
        //get group chats and chats on mounted await to subscribe to notification of groups
        await getChatGroups();
        await getChats();

        // Determine which id_login to use, to set the websocket, get it from the chas if the user has at least one
        if (chats.value.length) {
          userChatId.value = chats.value[0].id_login;
          setWebSocket();
        } else if (chatGroup.value.length && chatGroup.value[0].id_login) {
          userChatId.value = chatGroup.value[0].id_login;
          setWebSocket();
        }
      } catch (err) {
        console.error("Error loading chats/groups", err);
      }
    });

    onUnmounted(() => {
      //close socket if existing
      if (socket.value) {
        socket.value.close();
      }
    });

    // ----------------------------------------
    // METHODS
    // ----------------------------------------

    /**
     * Called when the collapse open/close state changes,
     * so we can emit and store the config for the widget.
     */
    const onUpdateStatus = async (value) => {
      emit("widget-collapsed", {
        id: props.id,
        property: "isOpen",
        location: props.location,
        value,
      });
    };

    /**
     * Opens WebSocket connection to Rocket.Chat. We check the user hash
     * to log in, then subscribe to events like 'stream-notify-user',
     * 'rooms-changed', etc.
     */
    const setWebSocket = () => {
      axios
        .get("/v1/services/user/hash")
        .then(({ data: response }) => {
          socket.value = new WebSocket(
            `wss://${window.__env.VUE_APP_ROCKET_CHAT_WS}/websocket`
          );

          // Fired when the WS connection is opened
          socket.value.onopen = function () {
            const connectMessage = {
              msg: "connect",
              version: "1",
              support: ["1"],
            };
            socket.value.send(JSON.stringify(connectMessage));
          };

          // Fired when a message is received
          socket.value.onmessage = function (event) {
            const data = JSON.parse(event.data);

            switch (data.msg) {
              case "connected": {
                // Perform login
                const loginMsg = {
                  msg: "method",
                  method: "login",
                  id: `${message_id.value++}`,
                  params: [
                    {
                      user: { username: `omniuser_${userData.value.id}` },
                      password: {
                        digest: response.data,
                        algorithm: "sha-256",
                      },
                    },
                  ],
                };
                socket.value.send(JSON.stringify(loginMsg));

                // Subscribe to user notifications
                const streamNotifyUser = {
                  msg: "sub",
                  id: `${message_id.value++}`,
                  name: "stream-notify-user",
                  params: [
                    userChatId.value,
                    { useCollection: false, args: [] },
                  ],
                };
                socket.value.send(JSON.stringify(streamNotifyUser));

                const streamNotifyUserSubscriptionsChanged = {
                  msg: "sub",
                  id: `${message_id.value++}`,
                  name: "stream-notify-user",
                  params: [
                    `${userChatId.value}/subscriptions-changed`,
                    { useCollection: false, args: [] },
                  ],
                };
                socket.value.send(
                  JSON.stringify(streamNotifyUserSubscriptionsChanged)
                );

                const streamNotifyUserRoomsChanged = {
                  msg: "sub",
                  id: `${message_id.value++}`,
                  name: "stream-notify-user",
                  params: [
                    `${userChatId.value}/rooms-changed`,
                    { useCollection: false, args: [] },
                  ],
                };
                socket.value.send(JSON.stringify(streamNotifyUserRoomsChanged));

                break;
              }

              case "ping": {
                // Rocket.Chat pings to check connection; respond with pong
                socket.value.send(JSON.stringify({ msg: "pong" }));
                break;
              }

              case "added": {
                // "added" means something new got added to the collection
                // Usually we subscribe to each existing chat's messages here
                chats.value.forEach((chat) => {
                  const message = {
                    msg: "sub",
                    id: `${message_id.value++}`,
                    name: "stream-room-messages",
                    params: [chat.room_id, false],
                  };
                  socket.value.send(JSON.stringify(message));
                });

                // Same for group rooms
                chatGroup.value.forEach((group) => {
                  const messageGroup = {
                    msg: "sub",
                    id: `${message_id.value++}`,
                    name: "stream-room-messages",
                    params: [group.roomId, false],
                  };
                  socket.value.send(JSON.stringify(messageGroup));
                });

                break;
              }

              case "changed": {
                // "changed" events come from:
                //  - 'stream-notify-user' (rooms-changed, subscriptions-changed)
                //  - 'stream-room-messages' (new messages)
                if (data.collection === "stream-notify-user") {
                  const eventName = data.fields.eventName;
                  // If a room was updated or removed, reload groups
                  if (
                    eventName.includes("rooms-changed") &&
                    data.fields.args[0] === "updated" &&
                    data.fields.args[1].msgs === 1
                  ) {
                    getChatGroups();
                  }
                  if (
                    eventName.includes("subscriptions-changed") &&
                    data.fields.args[0] === "removed"
                  ) {
                    getChatGroups();
                  }
                  // Mark unreads if user has not opened the chat
                  if (
                    userChatId.value + "/subscriptions-changed" ===
                    eventName
                  ) {
                    chats.value.find((chat) => {
                      if (chat.room_id === data.fields.args[1].rid) {
                        let openChat = false;

                        layoutApi.getCurrentTabData().slots.find((slot) => {
                          if ("CHAT_" + chat.id === slot.name) {
                            openChat = true;
                            readMessages(chat.room_id);
                          }
                        });

                        if (!openChat) {
                          chat.unreads = data.fields.args[1].unread;
                        }
                      }
                    });
                  }
                }

                if (data.collection === "stream-room-messages") {
                  // A new message was posted
                  const response_message = data.fields.args[0];
                  EventBus.emit(`new-message-${response_message.rid}`, {
                    message_id: response_message._id,
                    roomId: response_message.rid,
                    text: response_message.msg,
                    ts: new Date(response_message.ts.$date).toISOString(),
                    user_id: response_message.u._id,
                    username: response_message.u.username,
                    updated_at: new Date(
                      response_message._updatedAt.$date
                    ).toISOString(),
                    name: response_message.u.name,
                  });

                  // Update last_message in our local chats
                  chats.value.forEach((chat) => {
                    if (chat.room_id === response_message.rid) {
                      chat.last_message = response_message.msg;
                      chat.unread_from = new Date(
                        response_message.ts.$date
                      ).toISOString();
                    }
                  });

                  // Update last_message in our local groups
                  chatGroup.value.forEach((group) => {
                    if (group.roomId === response_message.rid) {
                      group.last_message = response_message.msg;
                      group.user = response_message.u.name;
                      group.updated_at = new Date(
                        response_message.ts.$date
                      ).toISOString();
                    }
                  });

                  // Decide if we trigger a toast notification
                  let isLoaded = false;
                  layoutApi.getCurrentTabData().slots.forEach((slot) => {
                    if (
                      slot.data.chat?.id &&
                      slot.type === SLOT_TYPES.CHAT &&
                      slot.name !== `CHAT_${slot.data.chat.id}` &&
                      response_message.u.username !==
                        `omniuser_${userData.value.id}`
                    ) {
                      isLoaded = true;
                    }
                  });
                  // If it's own message, don't notify
                  if (
                    response_message.u.username ===
                    `omniuser_${userData.value.id}`
                  ) {
                    isLoaded = true;
                  }

                  if (!isLoaded) {
                    toast({
                      component: ToastificationContent,
                      position: "top-right",
                      props: {
                        title: response_message.u.name,
                        variant: "infor",
                        icon: "BellIcon",
                        text: response_message.msg,
                        timeout: 5000,
                      },
                    });
                  }
                }
                break;
              }

              default:
                break;
            }
          };

          // On close event
          socket.value.onclose = function (event) {
            if (event.wasClean) {
              console.log(
                `[close] Connection closed cleanly, code=${event.code} reason=${event.reason}`
              );
            } else {
              console.log("[close] Connection was interrupted unexpectedly");
            }
          };
          socket.value.onerror = function () {
            console.log("[error] WebSocket error");
          };
        })
        .catch((error) => {
          console.log("Error obtaining user hash for chat:", error);
        });
    };

    const readMessages = (rid) => {
      const markAsReadMessage = {
        msg: "method",
        method: "readMessages",
        params: [rid],
        id: `${message_id.value++}`,
      };
      if (socket.value) {
        socket.value.send(JSON.stringify(markAsReadMessage));
      }
    };

    EventBus.on(`user-presence-chat`, (usersOnline) => {
      chats.value.forEach((chat) => {
        usersOnline.forEach((u) => {
          if (u.id === chat.id) {
            chat.online = 1;
          }
        });
      });
    });

    EventBus.on("subscribe-new-group", (roomId) => {
      console.log("subcribe to new group", roomId);
      if (socket.value) {
        socket.value.send(
          JSON.stringify({
            msg: "sub",
            id: `${message_id.value++}`,
            name: "stream-room-messages",
            params: [roomId, false],
          })
        );
      }
    });

    const getChatGroups = async () => {
      if (userData.value.enabled_chat && Number(window.__env.VUE_APP_ENABLED_CHAT)) {
        return store
          .dispatch("chat/getAll")
          .then((response) => {
            if (response.success) {
              chatGroup.value = response.data;
              chatAppGroup.value = chatGroup.value.filter(chat => chat.isFromApp === true);
              tempChatAppGroup.value = chatAppGroup.value
            }
          })
          .catch((e) => {
            console.error("Error getChatGroups()", e);
          });
      }
    };

    const getChats = async () => {
      if (userData.value.enabled_chat && Number(window.__env.VUE_APP_ENABLED_CHAT)) {
        try {
          const { data } = await axios.get("/v1/services/chat/chat", {
            params: {
              q: searchQuery.value,
              max: 1000,
            },
          });
          chats.value = data.data;
        } catch (e) {
          isLoading.value = true;
          error.value = true;
          console.error("Error getChats()", e);
        }
      }
    };

    const openChat = (chat) => {
      let isLoaded = false;
      layoutApi.getCurrentTabData().slots.forEach((slot) => {
        if (
          slot.type === SLOT_TYPES.CHAT &&
          (slot.name === `CHAT_${chat.id}` ||
            slot.name === `CHAT_${chat.roomId}`)
        ) {
          toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: this.$t("grid.slots.main.messages.widget_not_be_added"),
              variant: "warning",
              icon: "BellIcon",
              text: this.$t("grid.slots.main.messages.chat_widget_exists"),
              timeout: 5000,
            },
          });
          isLoaded = true;
        }
      });
      if (!isLoaded) {
        if (chat.roomId) {
          layoutApi.setSlotData(SLOT_TYPES.CHAT, `CHAT_${chat.roomId}`, {
            chat,
          });
        } else {
          layoutApi.setSlotData(SLOT_TYPES.CHAT, `CHAT_${chat.id}`, { chat });
          readMessages(chat.room_id);
        }
        store.dispatch("grid/updateUserConfig");
      }
    };

    const getUsers = async () => {
      try {
        let { data } = await axios.get("/v1/services/chat/chat-user");
        chatUsers.value = data.data.map((user) => ({
          value: user.userId,
          label: user.name,
        }));
      } catch (err) {
        console.error("Error getUsers()", err);
      }
    };

    const showAddModal = () => {
      resetModal();
      isAdd.value = true;
      previewModal.value.show();
    };
    const onCancel = () => {
      resetModal();
      previewModal.value.hide();
    };

    const showEditModal = async (group) => {
      isAdd.value = false;
      id_group.value = group.roomId;
      previewModal.value.show();
      try {
        let { data } = await axios.get(
          `/v1/services/chat/chat-group/getGroupUsers/${group.roomId}`
        );
        users.value = data.data.map((u) => ({
          value: u.userId,
          label: u.name,
        }));
        name.value = group.name.replace("_", " ");
      } catch (err) {
        console.error("Error getting group users", err);
      }
    };

    const onSubmit = async () => {
      const form = {
        name: name.value,
        users: users.value,
      };
      try {
        let result;
        if (isAdd.value) {
          result = await axios.post("/v1/services/chat/chat-group", form);
        } else {
          result = await axios.put(
            `/v1/services/chat/chat-group/${id_group.value}`,
            form
          );
        }
        if (result.data.success) {
          toast({
            component: ToastificationContent,
            props: {
              title: result.data.message,
              icon: "CheckIcon",
              variant: "success",
            },
          });
          getChatGroups();
          onCancel();
        } else {
          toast({
            component: ToastificationContent,
            props: {
              title: result.data.message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        }
      } catch (err) {
        console.error("Error onSubmit()", err);
      }
    };

    const confirmDelete = (group) => {
      Swal.fire({
        title: trans["messages.delete.title"],
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: trans["actions.delete"],
        cancelButtonText: trans["actions.cancel"],
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          axios
            .delete(`/v1/services/chat/chat-group/${group.roomId}`)
            .then((response) => {
              toast({
                component: ToastificationContent,
                props: {
                  title: response.data.message,
                  icon: "CheckIcon",
                  variant: "success",
                },
              });
              getChatGroups();
              onCancel();
            })
            .catch(() => {
              toast({
                component: ToastificationContent,
                props: {
                  title: response.data.message,
                  icon: "AlertTriangleIcon",
                  variant: "danger",
                },
              });
            });
        }
      });
    };

    const resetModal = () => {
      users.value = [];
      name.value = null;
    };

    const toggleSearchBar = () => {
      if (!searchBarEnabled.value) {
        searchBarEnabled.value = true;
      } else {
        searchBarEnabled.value = false;
        resetSearchBar();
      }
      isCollapseOpen.value = true;
    };

    const resetSearchBar = () => {
      currentPage.value = 1;
      searchQuery.value = "";
      chatGroup.value = store.state.chat.items;
      getChats();
    };

    const onTypeSearch = debounce(function (event) {
      if (event.target.value === "") {
        resetSearchBar();
        return;
      }
      const search = event.target.value.toLowerCase();
      chatGroup.value = store.state.chat.items.filter((group) =>
        group.name.toLowerCase().includes(search)
      );
      currentPage.value = 1;
      getChats();
    }, 800);

    const emergencyChatStatus = ref('');
    const optionsEmergencyChatStatus = ([
      { value: '', text: trans["chat.all"]},
      { value: 'assigned', text: trans["chat.assigned"]},
      { value: 'terminated', text: trans["chat.finalized"]}
    ]);

    watch([emergencyChatStatus], () => {
      if(!emergencyChatStatus.value){
        tempChatAppGroup.value = chatAppGroup.value;
      }
      else{
        tempChatAppGroup.value = chatAppGroup.value.filter(chat => chat.chat_status_group === emergencyChatStatus.value);
      }
    });

    return {
      collapsible,
      isConfigModeOn,
      sidebarMinWidthReached,
      isToolbarsCollapsed,
      message,
      perfectScrollbarSettings: { maxScrollbarLength: 150 },
      isCollapseOpen,
      chats,
      socket,
      message_id,
      userData,
      isLoading,
      error,
      chatGroup,
      chatUsers,
      users,
      name,
      id_group,
      isAdd,
      previewModal,
      //direct chats:
      perPage,
      currentPage,
      //group chats
      perPageG,
      currentPageG,
      //emergency app chats
      perPageApp,
      currentPageApp,

      searchBarWidth,
      chat_widget,
      searchBarEnabled,
      searchQuery,

      onUpdateStatus,
      formatDateToMonthShort,
      getChatGroups,
      openChat,
      getUsers,
      showAddModal,
      onCancel,
      showEditModal,
      resetModal,
      onSubmit,
      confirmDelete,
      toggleSearchBar,
      resetSearchBar,
      onTypeSearch,

      optionsEmergencyChatStatus,
      emergencyChatStatus,
      chatAppGroup,
      // Regular items:
      chatsItems,
      chatGroupItems,
      regularGroupsItems,
      appGroupsItems,

      //Computed slices for separate pagination:
      appGroupsPaginated,
      regularGroupsPaginated,
    };
  },
};
</script>

<style lang="scss">
@import "~@core/scss/base/pages/app-chat.scss";
@import "~@core/scss/base/pages/app-chat-list.scss";
</style>
